import { ClassData, MeetingDays } from "../../../common/dtos/classes.type";
import { DEFAULT_CATEGORY } from "../../../common/services/default-category.service";
import { submissionService } from "../../../common/services/submission.service";
import { trackingService } from "../../../common/services/tracking.service";
import { getClassesPerWeek } from "../../../common/utils/classes.util";
import {
  LOGIN_BUTTON_LINK,
  RYB_CLASSES_DATA,
  URL_PARAMETERS,
} from "../../../common/utils/const.util";
import {
  calculateTimeDifference,
  convertTo12HourFormat,
} from "../../../common/utils/date.util";
import {
  additionalUrlParameters,
  getPreloadClassId,
  getUrlParameterValue,
  parseRegistrationLink,
} from "../../../common/utils/url.util";
import { setPendingConversion } from "../../../common/utils/conversion.util";
import { getVariantId } from "../../../common/utils/variant.util";
import { openModal } from "../../../common/views/modal.view";
import { setGridClick } from "../../templates/trial/grid";
import { handlePreloadingOfShortcodes } from "../services/shortcodes.service";
import { writeCookie } from "../utils/cookie.util";
import { openModal as finderOpenModal } from "./modal-finder.view";
import { getCookie } from "../../campaign";
import {
  getSessionConfig,
  getSessionNow,
  getClassSessionInfo,
  formatSessionDate,
  getActiveDays,
  DAY_MAP,
  SessionConfig,
} from "../../../common/utils/session.util";
import {
  getActiveSessionConfig,
  isSessionToolbarActive,
} from "./session-toolbar.view";
import { getEffectiveClassPrice } from "../../../common/utils/pricing.util";

const SESSION_PORTAL_LOGIN_BASE =
  "https://app.jackrabbitclass.com/jr4.0/ParentPortal/Login";

interface RedirectModalConfig {
  enabled: boolean;
  title: string;
  description: string;
  ctaText: string;
  helperText: string;
  countdownText: string;
  countdownSeconds: number;
}

function getRedirectModalConfig(): RedirectModalConfig {
  const el = document.querySelector("#class-finder");
  return {
    enabled: el?.getAttribute("data-redirect-modal-enabled") === "yes",
    title: el?.getAttribute("data-redirect-modal-title") || "",
    description: el?.getAttribute("data-redirect-modal-description") || "",
    ctaText: el?.getAttribute("data-redirect-modal-cta-text") || "Enroll Now",
    helperText: el?.getAttribute("data-redirect-modal-helper-text") || "",
    countdownText: el?.getAttribute("data-redirect-modal-countdown-text") || "Redirecting in {seconds}...",
    countdownSeconds: parseInt(el?.getAttribute("data-redirect-modal-countdown-seconds") || "5", 10) || 5,
  };
}

let activeRedirectInterval: ReturnType<typeof setInterval> | null = null;

function showRedirectModal(config: RedirectModalConfig, redirectUrl: string, className: string): void {
  // Clear any previously running countdown from a prior modal open
  if (activeRedirectInterval) {
    clearInterval(activeRedirectInterval);
    activeRedirectInterval = null;
  }

  const $overlay = $(".redirect-modal-overlay");

  $overlay.find(".redirect-modal-title").text(config.title);
  $overlay.find(".redirect-modal-description").html(config.description);
  $overlay.find(".redirect-modal-cta").text(config.ctaText);
  $overlay.find(".redirect-modal-helper").text(config.helperText);

  $overlay.fadeIn(200);

  trackingService.pushEvent("ryb_redirect_modal_shown", {
    redirect_url: redirectUrl,
    class_name: className,
  });

  let remaining = config.countdownSeconds;
  const $countdown = $overlay.find(".redirect-modal-countdown");

  const doRedirect = () => {
    if (activeRedirectInterval) clearInterval(activeRedirectInterval);
    activeRedirectInterval = null;
    window.open(redirectUrl, "_blank");
    $overlay.fadeOut(200);
  };

  // CTA button → immediate redirect
  $overlay.find(".redirect-modal-cta").off("click").on("click", function () {
    trackingService.pushEvent("ryb_redirect_modal_cta_clicked", {
      redirect_url: redirectUrl,
      class_name: className,
    });
    doRedirect();
  });

  // Close button → dismiss
  $overlay.find(".redirect-modal-close").off("click").on("click", function () {
    trackingService.pushEvent("ryb_redirect_modal_dismissed", {
      redirect_url: redirectUrl,
      class_name: className,
    });
    if (activeRedirectInterval) clearInterval(activeRedirectInterval);
    activeRedirectInterval = null;
    $overlay.fadeOut(200);
  });

  // Click overlay background → dismiss
  $overlay.off("click").on("click", function (e) {
    if ($(e.target).hasClass("redirect-modal-overlay")) {
      trackingService.pushEvent("ryb_redirect_modal_dismissed", {
        redirect_url: redirectUrl,
        class_name: className,
      });
      if (activeRedirectInterval) clearInterval(activeRedirectInterval);
      activeRedirectInterval = null;
      $overlay.fadeOut(200);
    }
  });

  // Countdown timer
  if (remaining > 0) {
    $countdown.text(config.countdownText.replace("{seconds}", String(remaining))).show();
    activeRedirectInterval = setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        trackingService.pushEvent("ryb_redirect_modal_auto_redirect", {
          redirect_url: redirectUrl,
          class_name: className,
        });
        doRedirect();
      } else {
        $countdown.text(config.countdownText.replace("{seconds}", String(remaining)));
      }
    }, 1000);
  } else {
    $countdown.hide();
  }
}

function getClassWithRegistrationLink(data: ClassData[]): ClassData | null {
  return data.find((item) => !!item?.online_reg_link?.trim()) || null;
}

function getOrgIdFromRegistrationLink(registrationLink: string): string | null {
  const normalizedLink = registrationLink.replace(/\\/g, "").replace(/&amp;/g, "&");
  let orgId =
    getPreloadClassId(normalizedLink, "id") ||
    getPreloadClassId(normalizedLink, "orgID") ||
    getPreloadClassId(normalizedLink, "orgid");

  if (orgId) {
    return orgId;
  }

  try {
    const url = new URL(normalizedLink);
    orgId =
      url.searchParams.get("id") ||
      url.searchParams.get("orgID") ||
      url.searchParams.get("orgid");
    return orgId || null;
  } catch (_error) {
    return null;
  }
}

function getSessionPortalAutofillData(): {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
} | null {
  const rybUser = getCookie("rybUser");
  if (!rybUser) {
    return null;
  }

  try {
    const userData = JSON.parse(decodeURIComponent(escape(atob(rybUser))));
    const nameParts = (userData.name || "").trim().split(/\s+/).filter(Boolean);
    return {
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "",
      email: userData.email || "",
      phone: userData.phone || "",
    };
  } catch (_error) {
    return null;
  }
}

function buildSessionPortalBannerUrl(orgId: string, isNewUser: boolean): string {
  const url = new URL(SESSION_PORTAL_LOGIN_BASE);
  url.searchParams.set("orgID", orgId);

  if (isNewUser) {
    const autofillData = getSessionPortalAutofillData();
    url.searchParams.set("autofill", "true");
    url.searchParams.set("hearAbout", "Other");

    if (autofillData?.firstName) {
      url.searchParams.set("firstName", autofillData.firstName);
    }
    if (autofillData?.lastName) {
      url.searchParams.set("lastName", autofillData.lastName);
    }
    if (autofillData?.email) {
      url.searchParams.set("email", autofillData.email);
    }
    if (autofillData?.phone) {
      url.searchParams.set("phone", autofillData.phone);
    }
  } else {
    url.searchParams.set("notice", "true");
  }

  return url.toString();
}

function buildSessionPortalBannerRedirectUrl(data: ClassData[]): string | null {
  const classWithLink = getClassWithRegistrationLink(data);
  if (!classWithLink) {
    return null;
  }

  const userParam = getUrlParameterValue(URL_PARAMETERS.USER);
  const isNewUser = userParam === "false";
  const orgId = getOrgIdFromRegistrationLink(classWithLink.online_reg_link);
  if (!orgId) {
    return null;
  }

  return buildSessionPortalBannerUrl(orgId, isNewUser);
}

function setSessionPortalBanner(data: ClassData[]): void {
  const $finder = $("#class-finder");
  const $banner = $finder.find(".session-portal-banner");
  const $cta = $banner.find(".session-portal-banner-cta");

  if (!$banner.length || !$cta.length) {
    return;
  }

  const userParam = getUrlParameterValue(URL_PARAMETERS.USER);
  const isNewUser = userParam === "false";
  const loginText = $finder.attr("data-session-portal-banner-login-cta") || "Login";
  const signupText = $finder.attr("data-session-portal-banner-signup-cta") || "Sign up";
  $cta.text(isNewUser ? signupText : loginText);

  const redirectUrl = buildSessionPortalBannerRedirectUrl(data);
  $cta.off("click.rybSessionPortalBanner");

  if (!redirectUrl) {
    $cta.prop("disabled", true).addClass("disabled").attr("aria-disabled", "true");
    return;
  }

  $cta.prop("disabled", false).removeClass("disabled").removeAttr("aria-disabled");
  $cta.on("click.rybSessionPortalBanner", function (event) {
    event.preventDefault();
    trackingService.pushEvent("ryb_session_portal_banner_clicked", {
      user_type: isNewUser ? "new" : "registered",
      destination_url: redirectUrl,
    });
    window.open(redirectUrl, "_blank");
  });
}

function applyEnhancedCardDesign(
  cellElement: JQuery,
  option: ClassData,
  now: Date
): void {
  // Add the shared visual class
  cellElement.addClass("enhanced-card");

  // Color-code time badge: green if openings, gray if waitlist
  const hasOpenings = option.openings.calculated_openings > 0;
  cellElement
    .find(".class-start")
    .addClass(hasOpenings ? "time-available" : "time-waitlist");

  // Calculate next class date from meeting_days (simple weekly cycle)
  const activeDays = getActiveDays(option.meeting_days);
  if (activeDays.length > 0) {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + i);
      if (activeDays.includes(checkDate.getDay())) {
        const isToday = i === 0;
        const dateLabel = isToday ? "Today" : formatSessionDate(checkDate);
        cellElement
          .find(".session-next-class")
          .html(`Next Class \u2013 <span>${dateLabel}</span>`)
          .show();
        break;
      }
    }
  }

  // Build detail rows with SVG icons
  const classTimeText = cellElement.find(".class-time").text();
  let detailItems = "";

  const svgSchedule = `<svg class="session-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>`;
  const svgCalendar = `<svg class="session-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>`;

  // Duration row
  if (classTimeText) {
    detailItems += `<div class="session-detail-item">${svgSchedule}<span>${classTimeText}</span></div>`;
  }

  // Classes per week row with day names
  const dayLabels: Record<string, string> = {
    sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed",
    thu: "Thu", fri: "Fri", sat: "Sat",
  };
  const activeDayNames = (Object.keys(option.meeting_days) as (keyof MeetingDays)[])
    .filter((k) => option.meeting_days[k])
    .map((k) => dayLabels[k]);
  if (activeDayNames.length > 0) {
    const count = activeDayNames.length;
    const classLabel = count === 1 ? "class" : "classes";
    const perWeekText = `${count} ${classLabel} a week on ${activeDayNames.join(", ")}`;
    detailItems += `<div class="session-detail-item session-detail-muted">${svgCalendar}<span>${perWeekText}</span></div>`;
  }

  // Insert details section after .class-data
  if (detailItems) {
    cellElement
      .find(".class-data")
      .after(`<div class="session-details">${detailItems}</div>`);
  }
}

export function setResultGridView(data: ClassData[]) {
  setSessionPortalBanner(data);

  $("#class-finder .grid-results .monday-results").empty();
  $("#class-finder .grid-results .tuesday-results").empty();
  $("#class-finder .grid-results .wednesday-results").empty();
  $("#class-finder .grid-results .thursday-results").empty();
  $("#class-finder .grid-results .friday-results").empty();
  $("#class-finder .grid-results .saturday-results").empty();
  $("#class-finder .grid-results .sunday-results").empty();

  // Centralised empty-state handling: when the filtered data is empty,
  // hide the day grid and show the no-results fallback. This runs for both
  // initial-load renders (via processResult) and dynamic filter changes
  // (e.g. the user picks an over-restrictive combination from the filter
  // pills) — every code path eventually calls setResultGridView().
  if (!data.length) {
    showNoResultsFallback();
    return;
  }
  hideNoResultsFallback();

  const isUser = getUrlParameterValue("user");
  const classFinderEl = document.querySelector("#class-finder");
  const enrollDisabled = classFinderEl?.getAttribute("data-enroll-disabled") === "yes";
  const enrollDisabledText = classFinderEl?.getAttribute("data-enroll-disabled-text") || "Coming Soon";
  const sessionConfig: SessionConfig = isSessionToolbarActive()
    ? (getActiveSessionConfig() || getSessionConfig())
    : getSessionConfig();
  const sessionNow = getSessionNow();

  const template = document.getElementById(
    "result-grid-cell-template"
  )?.innerHTML;
  if (!template) {
    return;
  }

  const initiatedShortcodes: string[] = [];

  $.each(data, (_index, option) => {
    const rybCategoryData = RYB_CLASSES_DATA.classes?.find(
      (category) => category.name === option[DEFAULT_CATEGORY]
    );
    const categoryName =
      rybCategoryData?.display_name || option[DEFAULT_CATEGORY];
    const effectivePrice = getEffectiveClassPrice(option, rybCategoryData);

    const categoryLocation = option?.location;
    const status = rybCategoryData?.status || "Hide";
    if (status === "Hide" || status === "Invite-only (unlisted)") {
      return; // ignore the box if it's hidden or unlisted
    }
    const cellElement = $(template);
    const totalTime = calculateTimeDifference(
      option.start_time,
      option.end_time
    );
    const time = option?.start_time
      ? convertTo12HourFormat(option.start_time)
      : { time: "", period: "" };
    cellElement.find(".class-name").text(`${categoryName}`);
    cellElement
      .find("div.class-start")
      .html(
        `<span class="time">${time.time}</span> <span class="period">${time.period}</span>`
      );
    cellElement
      .find("div.classes-per-week")
      .text(
        `${
          getClassesPerWeek(option.meeting_days) > 1
            ? `${getClassesPerWeek(option.meeting_days)} classes per week`
            : "1 class per week"
        }`
      );
    if ($(".result-grid-cell .location-name")) {
      cellElement
        .find(".location-name")
        .text(
          option.location_city && option.location_name
            ? `${option.location_city}, ${option.location_state}`
            : option.location_name
        );
    }
    cellElement.find("div.class-time").text(`${totalTime}`);
    cellElement.find("span.class-price").text(`${effectivePrice}`);
    if (status === "Active") {
      cellElement.find(".control-button.invite-only").remove();
      if (option.openings.calculated_openings > 0) {
        cellElement
          .find("div.control-button .enroll-class.enroll")
          .removeClass("d-none");

        cellElement
          .find("div.control-button .enroll-class.trial")
          .removeClass("d-none");
      } else {
        cellElement
          .find("div.control-button .enroll-class.waitlist")
          .removeClass("d-none");
        cellElement
          .find("div.control-button .enroll-class.trial-full")
          .removeClass("d-none");
      }
    } else if (status === "Invite-only") {
      cellElement.find(".control-button.active").remove();
      if (
        rybCategoryData?.invite_only_funnel_shortcode &&
        !initiatedShortcodes.includes(rybCategoryData.display_name)
      ) {
        handlePreloadingOfShortcodes(
          rybCategoryData?.invite_only_funnel_shortcode
        );
        initiatedShortcodes.push(rybCategoryData.display_name);
      }
    } else {
      cellElement.find(".control-button.active").remove();
      cellElement.find(".control-button.invite-only").remove();
      // don't expect anything because class should already be hidden
    }

    if (enrollDisabled) {
      cellElement.find(".button-section").html(
        `<div class="control-button"><button class="enroll-class disabled-enroll" disabled>${enrollDisabledText}</button></div>`
      );
    }

    // Apply enhanced card design to ALL cards (monthly + session)
    applyEnhancedCardDesign(cellElement, option, sessionNow);

    if (sessionConfig.enabled && sessionConfig.startDate && sessionConfig.endDate) {
      const sessionInfo = getClassSessionInfo(
        option.meeting_days,
        option.start_time,
        option.end_time,
        effectivePrice,
        sessionConfig,
        sessionNow
      );

      // Add session status class to the card
      cellElement.addClass(`session-status-${sessionInfo.status}`);

      // Status pill + next class line
      if (sessionInfo.status === "upcoming") {
        cellElement
          .find(".session-start-date")
          .text(`Starts ${formatSessionDate(sessionInfo.firstClassDate)}`)
          .show();
      } else if (sessionInfo.status === "going") {
        cellElement
          .find(".session-start-date")
          .text(`Ongoing`)
          .show();
        // Show next class date
        if (sessionInfo.nextClassDate) {
          const today = new Date(sessionNow);
          today.setHours(0, 0, 0, 0);
          const nextDay = new Date(sessionInfo.nextClassDate);
          nextDay.setHours(0, 0, 0, 0);
          const isToday = today.getTime() === nextDay.getTime();
          const dateLabel = isToday ? "Today" : formatSessionDate(sessionInfo.nextClassDate);
          cellElement
            .find(".session-next-class")
            .html(`Next Class \u2013 <span>${dateLabel}</span>`)
            .show();
        }
        // Use prorated price (rounded to nearest dollar)
        cellElement.find("span.class-price").text(`${Math.round(sessionInfo.proratedPrice)}`);
        cellElement.find(".class-tuition").addClass("prorated");
        if (!cellElement.find(".original-price").length) {
          cellElement.find(".class-tuition").append(
            `<span class="original-price">$${sessionInfo.totalPrice}</span>`
          );
        }
        if (!cellElement.find(".prorated-label").length) {
          cellElement.find(".class-tuition").after(
            `<span class="prorated-label">Prorated price</span>`
          );
        }
      } else if (sessionInfo.status === "past") {
        cellElement
          .find(".session-start-date")
          .text("Session ended")
          .show();
        cellElement.find(".session-next-class").hide();
        if (!enrollDisabled) {
          cellElement.find(".button-section").html(
            `<div class="control-button"><button class="enroll-class disabled-enroll" disabled>Completed</button></div>`
          );
        }
      }

      // Add session-specific detail row (classes remaining/total)
      const $details = cellElement.find(".session-details");
      const svgEventRepeat = `<svg class="session-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 12V6c0-1.1-.9-2-2-2h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h7v-2H5V10h14v2h2zm-5.36 8a3.504 3.504 0 0 0 6.86-1c0-1.93-1.57-3.5-3.5-3.5-.95 0-1.82.38-2.45 1H18v2h-4v-4h2v1.45c.9-.89 2.14-1.45 3.5-1.45 2.76 0 5 2.24 5 5a5.51 5.51 0 0 1-8.86 4z"/></svg>`;

      if (sessionInfo.status === "going") {
        const classesLabel = sessionInfo.remainingClasses === 1 ? "class" : "classes";
        $details.find(".session-detail-muted").before(
          `<div class="session-detail-item session-detail-blue">${svgEventRepeat}<span>${sessionInfo.remainingClasses}/${sessionConfig.totalClasses} ${classesLabel} left</span></div>`
        );
      } else if (sessionInfo.status === "upcoming") {
        $details.find(".session-detail-muted").before(
          `<div class="session-detail-item">${svgEventRepeat}<span>${sessionConfig.totalClasses} classes</span></div>`
        );
      }

      // Change enroll button text
      cellElement.find(".enroll-class.enroll").text("Enroll Now");
    }

    function clickActiveClass(
      event: JQuery.ClickEvent<HTMLElement, undefined, HTMLElement, HTMLElement>
    ) {
      const isEnroll = option.openings.calculated_openings > 0;
      const actionType = isEnroll ? "enroll" : "waitlist";
      trackingService.pushEvent(
        isEnroll ? "ryb_enroll_clicked" : "ryb_waitlist_clicked",
        {
          class_name: categoryName,
          class_id: option.id,
          class_day: Object.keys(option.meeting_days).find(
            (d) => (option.meeting_days as any)[d]
          ) || "",
        }
      );
      submissionService.updateSubmission({
        selected_class_name: categoryName,
        selected_class_id: String(option.id),
        selection_type: actionType,
        funnel_last_step: actionType,
      });

      const classId = getPreloadClassId(
        option.online_reg_link,
        "preLoadClassID"
      );
      const orgId = getPreloadClassId(option.online_reg_link, "id");
      const triggerContactModal =
        getUrlParameterValue(URL_PARAMETERS.CONTACT) === "false";

      writeCookie(
        "classData",
        btoa(
          unescape(encodeURIComponent(JSON.stringify({
            ...option,
            location: categoryLocation,
            category2: categoryName,
          })))
        ),
        365
      );

      const setConversionAndRedirect = (url: string) => {
        setPendingConversion({
          submissionId: submissionService.getSubmissionId(),
          className: categoryName,
          classId: String(option.id),
          selectionType: actionType,
          funnelType: "finder",
          ghlContactId: localStorage.getItem("ghlContactId"),
        });
        trackingService.pushEvent("ryb_external_redirect", {
          destination_url: url,
          class_name: categoryName,
        });

        const modalConfig = getRedirectModalConfig();
        if (modalConfig.enabled) {
          showRedirectModal(modalConfig, url, categoryName);
        } else {
          window.open(url, "_blank");
        }
      };

      if (triggerContactModal) {
        openModal(
          {
            ...option,
            location: categoryLocation,
            category2: categoryName,
          },
          "form"
        );
        return;
      }
      if (!option.online_reg_link || option.online_reg_link === " ") {
        const rybCategoryData = RYB_CLASSES_DATA.classes?.find(
          (category) => category.name === option[DEFAULT_CATEGORY]
        );

        const contactId = localStorage.getItem('ghlContactId');
        if (contactId) {
          $.ajax({
            url: `https://${window.location.host}/wp-json/ryb/v1/ghl?timestamp=${Date.now()}`,
            method: "POST",
            contentType: "application/json",
            data: JSON.stringify({
              request_url: `https://services.leadconnectorhq.com/contacts/${contactId}`,
              include_location_id: false,
              method: "PUT",
              body: {
                customFields: [
                  {
                    key: "selected_class_id",
                    field_value: option.id,
                  },
                  {
                    key: "selected_class_url",
                    field_value: additionalUrlParameters(
                      rybCategoryData?.fallback_url || "",
                      effectivePrice.toString(),
                      option.category1,
                      option.category2
                    ),
                  },
                  {
                    key: "last_funnel_url",
                    field_value: new URL(window.location.href),
                  },
                  {
                    key: "step_1",
                    field_value: "Step 5 | Click to Enroll/Waitlist",
                  },
                ],
              }
            })
          })
        }

        const fallbackUrl = additionalUrlParameters(
            rybCategoryData?.fallback_url || "",
            effectivePrice.toString(),
            option.category1,
            option.category2
          );
        if (fallbackUrl) {
          setConversionAndRedirect(fallbackUrl);
        } else {
          console.error("No registration or fallback URL available for class:", categoryName);
        }

        return;
      }
      event.preventDefault();
      if (isUser === "true") {
        setConversionAndRedirect(
          `${LOGIN_BUTTON_LINK}?orgId=${orgId}&regcid=${classId}`
        );
        return;
      }
      if (isUser === "false") {
        const contactId = localStorage.getItem('ghlContactId');
        if (contactId) {
          $.ajax({
            url: `https://${window.location.host}/wp-json/ryb/v1/ghl?timestamp=${Date.now()}`,
            method: "POST",
            contentType: "application/json",
            data: JSON.stringify({
              request_url: `https://services.leadconnectorhq.com/contacts/${contactId}`,
              include_location_id: false,
              method: "PUT",
              body: {
                customFields: [
                  {
                    key: "selected_class_id",
                    field_value: option.id,
                  },
                  {
                    key: "selected_class_url",
                    field_value: parseRegistrationLink(
                      option.online_reg_link,
                      effectivePrice.toString(),
                      option.category1,
                      option.category2)
                  },
                  {
                    key: "last_funnel_url",
                    field_value: new URL(window.location.href),
                  },
                  {
                    key: "step_1",
                    field_value: "Step 5 | Click to Enroll/Waitlist",
                  },
                ],
              }
            })
          })
        }
        
        const regUrl = parseRegistrationLink(
            option.online_reg_link,
            effectivePrice.toString(),
            option.category1,
            option.category2
          );
        setConversionAndRedirect(regUrl);
        return;
      }
      if (!isUser) {
        const directUrl = parseRegistrationLink(
            option.online_reg_link,
            effectivePrice.toString(),
            option.category1,
            option.category2
          );
        setConversionAndRedirect(directUrl);
        return;
      }

      openModal({
        ...option,
        location: categoryLocation,
        category2: categoryName,
      });
    }

    if (!enrollDisabled) {
      cellElement.find(".enroll").on("click", clickActiveClass);
      cellElement.find(".waitlist").on("click", clickActiveClass);

      cellElement.find("button.main-info").on("click", function (event) {
        event.preventDefault();
        trackingService.pushEvent("ryb_invite_only_clicked", {
          class_name: categoryName,
        });
        submissionService.updateSubmission({
          selected_class_name: categoryName,
          selection_type: "invite_only",
          funnel_last_step: "invite_only",
        });

        openModal(
          {
            ...option,
            location: categoryLocation,
            category2: categoryName,
          },
          "invite-only"
        );
      });
      cellElement.find(".trial").on("click", function (clickEvent) {
        trackingService.pushEvent("ryb_trial_clicked", {
          class_name: categoryName,
          class_id: option.id,
        });
        submissionService.updateSubmission({
          selected_class_name: categoryName,
          selected_class_id: String(option.id),
          selection_type: "trial",
          funnel_last_step: "trial",
        });
        setGridClick(clickEvent, option);
      });
    }

    cellElement.find(".info").on("click", function (event) {
      event.preventDefault();
      trackingService.pushEvent("ryb_class_info_clicked", {
        class_name: categoryName,
        class_id: option.id,
      });
      finderOpenModal({
        ...option,
        location: categoryLocation,
        category2: categoryName,
      });
    });

    if (option.meeting_days.mon) {
      $(".grid-results .monday-results").append(cellElement);
    }
    if (option.meeting_days.tue) {
      $(".grid-results .tuesday-results").append(cellElement);
    }
    if (option.meeting_days.wed) {
      $(".grid-results .wednesday-results").append(cellElement);
    }
    if (option.meeting_days.thu) {
      $(".grid-results .thursday-results").append(cellElement);
    }
    if (option.meeting_days.fri) {
      $(".grid-results .friday-results").append(cellElement);
    }
    if (option.meeting_days.sat) {
      $(".grid-results .saturday-results").append(cellElement);
    }
    if (option.meeting_days.sun) {
      $(".grid-results .sunday-results").append(cellElement);
    }

  });

  // Bind form modal handlers ONCE outside the loop (use .off first to prevent accumulation)
  $("#form .ryb-connect-modal .register-button").off("click").on("click", (event) => {
    event.preventDefault();
    $("#form .ryb-connect-modal .registration-form").addClass("show-field");
    $("#form .ryb-connect-modal .modal-content").removeClass("show-field");
  });
  $("#form.ryb-connect-overlay").off("click").on("click", (event) => {
    if ($(event.target).hasClass("show")) {
      $("#form .ryb-connect-modal .registration-form").removeClass(
        "show-field"
      );
      $("#form .ryb-connect-modal .modal-content").addClass("show-field");
    }
  });

  setNoClassesContent();
  updateDayClassCounts();
}

export function setToggleTableFunctionality() {
  $(".grid-results .accordion")
    .off("click")
    .on("click", function () {
      $(this).toggleClass("open");
      trackingService.pushEvent("ryb_accordion_toggled", {
        day: $(this).text().trim(),
        action: $(this).hasClass("open") ? "open" : "close",
      });
    });

  // Allow tapping the day heading (h4) to toggle accordion on mobile
  $(".grid-results > div > h4")
    .off("click")
    .on("click", function () {
      $(this).siblings(".accordion").trigger("click");
    });
}

function setNoClassesContent() {
  if (!$(".grid-results .monday-results > div").length) {
    $("#class-finder .grid-results .monday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
  if (!$(".grid-results .tuesday-results > div").length) {
    $("#class-finder .grid-results .tuesday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
  if (!$(".grid-results .wednesday-results > div").length) {
    $("#class-finder .grid-results .wednesday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
  if (!$(".grid-results .thursday-results > div").length) {
    $("#class-finder .grid-results .thursday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
  if (!$(".grid-results .friday-results > div").length) {
    $("#class-finder .grid-results .friday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
  if (!$(".grid-results .saturday-results > div").length) {
    $("#class-finder .grid-results .saturday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
  if (!$(".grid-results .sunday-results > div").length) {
    $("#class-finder .grid-results .sunday-results").html(
      `<div class="no-result"> No classes found</div>`
    );
  }
}

function updateDayClassCounts() {
  $(".grid-results > div").each(function () {
    const $daySection = $(this);
    const $h4 = $daySection.find("> h4");
    const $resultsContainer = $daySection.find("[class$='-results']");
    const classCount = $resultsContainer.find(".result-grid-cell").length;

    // Remove any existing count badge
    $h4.find(".day-count").remove();

    if (classCount > 0) {
      $h4.append(
        `<span class="day-count">${classCount} class${classCount !== 1 ? "es" : ""}</span>`
      );
      $daySection.removeClass("no-classes-day");
    } else {
      $h4.append(`<span class="day-count">No classes</span>`);
      $daySection.addClass("no-classes-day");
    }
  });
}

/* ============================================================================
 * No-results fallback
 *
 * The fallback panel is shown whenever a filter combination produces zero
 * classes — both on initial render (e.g. a polluted `Cat2=…` URL) and after
 * a dynamic filter change in the pills.
 *
 * Critical CSS is injected via a <style> block alongside the markup so it
 * survives LiteSpeed's "Unused CSS" optimization (which strips rules whose
 * selectors aren't found in the initial server-rendered HTML, and was the
 * root cause of the unstyled rendering on texasdreams.com).
 * ========================================================================== */

const NO_RESULTS_FALLBACK_CSS = `
.no-results-fallback {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 360px;
  padding: 40px 20px;
  font-family: var(--ryb-font-family, 'DM Sans', sans-serif);
  width: 100%;
  box-sizing: border-box;
}
.no-results-fallback[hidden] { display: none !important; }
.no-results-fallback__inner {
  max-width: 420px;
  text-align: center;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  padding: 36px 32px 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px -8px rgba(0, 0, 0, 0.08);
}
.no-results-fallback__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.04);
  color: rgba(15, 23, 42, 0.55);
  margin-bottom: 16px;
}
.no-results-fallback__title {
  font-size: 17px !important;
  font-weight: 700 !important;
  color: #0f172a !important;
  letter-spacing: -0.2px;
  margin: 0 0 6px !important;
  line-height: 1.3;
}
.no-results-fallback__desc {
  font-size: 13px !important;
  color: rgba(0, 0, 0, 0.55) !important;
  line-height: 1.5;
  margin: 0 0 20px !important;
  font-weight: 400;
}
.no-results-fallback__actions {
  display: inline-flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.no-results-fallback__reset,
.no-results-fallback__back {
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1px;
  padding: 10px 18px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}
.no-results-fallback__reset {
  background: var(--ryb-primary-color, #102a43);
  color: #fff;
  border-color: var(--ryb-primary-color, #102a43);
}
.no-results-fallback__reset:hover { transform: translateY(-1px); }
.no-results-fallback__back {
  background: #fff;
  color: rgba(0, 0, 0, 0.7);
  border-color: rgba(0, 0, 0, 0.15);
}
.no-results-fallback__back:hover { background: rgba(0, 0, 0, 0.04); border-color: rgba(0, 0, 0, 0.3); }
@media only screen and (max-width: 600px) {
  .no-results-fallback__inner { padding: 28px 22px 22px; }
  .no-results-fallback__actions { flex-direction: column; width: 100%; }
  .no-results-fallback__reset,
  .no-results-fallback__back { width: 100%; }
}
#class-finder .grid-view.is-empty .grid-results,
#ryb-connect .grid-view.is-empty .grid-results { display: none !important; }
`;

const ALLOWED_FILTER_KEYS_FOR_FALLBACK = new Set([
  URL_PARAMETERS.AGE,
  URL_PARAMETERS.GENDER,
  URL_PARAMETERS.LOCATION,
  URL_PARAMETERS.CATEGORY,
  URL_PARAMETERS.DAYS,
  URL_PARAMETERS.SESSION,
]);

function getActiveFilterSummary(): string {
  const params = new URLSearchParams(window.location.search);
  const labels: string[] = [];
  params.forEach((value, key) => {
    if (!ALLOWED_FILTER_KEYS_FOR_FALLBACK.has(key)) return;
    if (!value || String(value).toLowerCase() === "all") return;
    if (key === URL_PARAMETERS.AGE) labels.push("age");
    else if (key === URL_PARAMETERS.GENDER) labels.push("gender");
    else if (key === URL_PARAMETERS.LOCATION) labels.push("location");
    else if (key === URL_PARAMETERS.CATEGORY) labels.push("class type");
    else if (key === URL_PARAMETERS.DAYS) labels.push("days");
    else if (key === URL_PARAMETERS.SESSION) labels.push("session");
  });
  return labels.join(", ");
}

function clearWizardFilterParams() {
  const url = new URL(window.location.href);
  ALLOWED_FILTER_KEYS_FOR_FALLBACK.forEach((key) => url.searchParams.delete(key));
  history.replaceState({}, "", url.toString());
}

export function showNoResultsFallback() {
  // 1. Inject critical CSS once per session.
  if (!document.getElementById("ryb-connect-no-results-css")) {
    const styleEl = document.createElement("style");
    styleEl.id = "ryb-connect-no-results-css";
    styleEl.textContent = NO_RESULTS_FALLBACK_CSS;
    document.head.appendChild(styleEl);
  }

  // 2. Hide the day grid (the .grid-results inside .grid-view).
  $("#class-finder .grid-view, #ryb-connect .grid-view").addClass("is-empty");

  // 3. Mount the fallback INSIDE .grid-view so it visually occupies the
  //    same space as the (now hidden) day grid. Falling back to other
  //    candidates only when .grid-view doesn't exist on the page (e.g.
  //    custom layouts).
  const $gridView = $("#class-finder .grid-view, #ryb-connect .grid-view").first();
  const $resultArea = $gridView.length
    ? $gridView
    : $(
        "#class-finder .step-view.result, #class-finder #ryb-openings-result, #ryb-openings-result"
      ).first();
  if (!$resultArea.length) return;

  // Remove any stale fallback markup that earlier code may have placed
  // outside .grid-view (e.g. in #ryb-openings-result), so we don't end up
  // with two panels stacked vertically.
  $(".no-results-fallback").not($resultArea.find(".no-results-fallback")).remove();

  let $fallback = $resultArea.find(".no-results-fallback");
  if (!$fallback.length) {
    $fallback = $(`
      <div class="no-results-fallback" role="status">
        <div class="no-results-fallback__inner">
          <div class="no-results-fallback__icon" aria-hidden="true">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 class="no-results-fallback__title">No classes match these filters</h3>
          <p class="no-results-fallback__desc"></p>
          <div class="no-results-fallback__actions">
            <button type="button" class="no-results-fallback__reset">Clear filters</button>
            <button type="button" class="no-results-fallback__back">Start over</button>
          </div>
        </div>
      </div>
    `);
    $resultArea.append($fallback);

    // Bind once.
    $fallback.find(".no-results-fallback__reset").on("click", () => {
      clearWizardFilterParams();
      // Re-trigger filtering with the cleared URL by calling whatever
      // result-renderer is available (filter-top.view binds these handlers).
      const evt = new CustomEvent("ryb:results-cleared");
      document.dispatchEvent(evt);
      // Also force a re-render via setResultGridView with full data.
      const RYBConnectClasses = (window as any).RYBConnectClasses;
      if (RYBConnectClasses?.instance) {
        RYBConnectClasses.instance.getFilteredDataByURLParams().then((d: any) => setResultGridView(d));
      } else {
        // Fallback: hard reload with cleared params.
        window.location.reload();
      }
    });
    $fallback.find(".no-results-fallback__back").on("click", () => {
      try {
        document.cookie = "rybFilters=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
      } catch {}
      const url = new URL(window.location.href);
      url.search = "";
      window.location.href = url.toString();
    });
  }

  $fallback.find(".no-results-fallback__desc").text(
    getActiveFilterSummary()
      ? `Try removing one of: ${getActiveFilterSummary()}.`
      : "Try a wider age range or different class type."
  );
  $fallback.removeAttr("hidden").show();
}

export function hideNoResultsFallback() {
  $(".no-results-fallback").attr("hidden", "true").hide();
  $("#class-finder .grid-view, #ryb-connect .grid-view").removeClass("is-empty");
}
